import React, { createContext, useContext, useReducer, useEffect } from 'react';

const SubscriptionContext = createContext();

const STORAGE_KEY = 'kito_subscription_state';

const initialState = {
  items: [], // Paketteki ürünler
  subscriptionType: null, // Abonelik tipi (haftalık, aylık, vs)
  step: 'products', // Mevcut adım (products, package, subscription-type, payment, complete)
  subscriptionPlan: null, // Abonelik planı
  outOfStockItems: [], // Stokta olmayan ürünleri takip etmek için
  orderInfo: null, // Add this new field
  isEditing: false, // Abonelik düzenleme modu
};

// Session'dan state'i yükle
const loadInitialState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    return savedState ? JSON.parse(savedState) : initialState;
  } catch (error) {
    console.error('Failed to load state from localStorage:', error);
    return initialState;
  }
};

function subscriptionReducer(state, action) {
  let newState;
  
  switch (action.type) {
    case 'ADD_TO_PACKAGE':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      // Stok kontrolü
      if (action.payload.stock !== undefined && action.payload.stock <= 0) {
        // Stokta yoksa ekleme yapma
        return state;
      }
      
      if (existingItem) {
        // Check if a specific quantity was provided in the payload
        const newQuantity = action.payload.quantity ? 
          action.payload.quantity : 
          existingItem.quantity + 1;
          
        // Stok kontrolü
        if (action.payload.stock !== undefined && newQuantity > action.payload.stock) {
          // Yeterli stok yoksa miktarı artırma
          return state;
        }
        
        newState = {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: newQuantity }
              : item
          ),
        };
      } else {
        // Use the quantity from payload if provided, otherwise default to 1
        const quantity = action.payload.quantity || 1;
        
        newState = {
          ...state,
          items: [...state.items, { ...action.payload, quantity }],
        };
      }
      break;

    case 'REMOVE_FROM_PACKAGE':
      newState = {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
      };
      break;

    case 'UPDATE_QUANTITY':
      // Stok kontrolü
      const itemToUpdate = state.items.find(item => item.id === action.payload.id);
      if (itemToUpdate && itemToUpdate.stock !== undefined && action.payload.quantity > itemToUpdate.stock) {
        // Yeterli stok yoksa miktarı güncelleme
        return state;
      }
      
      newState = {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
      break;

    case 'SET_SUBSCRIPTION_TYPE':
      newState = {
        ...state,
        subscriptionType: action.payload,
      };
      break;

    case 'SET_STEP':
      newState = {
        ...state,
        step: action.payload,
      };
      break;

    case 'RESET_PACKAGE':
      // Tüm state'i sıfırla
      newState = {
        ...initialState,
        orderInfo: null // Özellikle orderInfo'yu da sıfırla
      };
      
      // Session storage'dan da temizle
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('Failed to clear localStorage:', error);
      }
      break;

    case 'SET_SUBSCRIPTION_PLAN':
      newState = {
        ...state,
        subscriptionPlan: action.payload,
      };
      break;

    case 'UPDATE_OUT_OF_STOCK_ITEMS':
      newState = {
        ...state,
        outOfStockItems: action.payload
      };
      break;

    case 'SET_ORDER_INFO':
      newState = {
        ...state,
        orderInfo: action.payload,
      };
      break;

    case 'SET_IS_EDITING':
      newState = {
        ...state,
        isEditing: action.payload,
      };
      break;

    default:
      return state;
  }

  // Her state değişiminde session storage'ı güncelle
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  } catch (error) {
    console.error('Failed to save state to localStorage:', error);
  }

  return newState;
}

export function SubscriptionProvider({ children }) {
  const [state, dispatch] = useReducer(subscriptionReducer, loadInitialState());

  // Sayfa yenilendiğinde state'i session'dan yükle
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        const newState = JSON.parse(e.newValue);
        if (newState) {
          Object.keys(newState).forEach(key => {
            dispatch({ type: `SET_${key.toUpperCase()}`, payload: newState[key] });
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Stok kontrolü yapan fonksiyon
  const checkStockStatus = () => {
    const outOfStock = state.items.filter(item => item.stock < item.quantity);
    dispatch({ type: 'UPDATE_OUT_OF_STOCK_ITEMS', payload: outOfStock });
    return outOfStock;
  };

  // Stok durumu mesajını oluşturan fonksiyon
  const getStockWarningMessage = () => {
    const outOfStock = checkStockStatus();
    if (outOfStock.length === 0) return null;

    return {
      title: 'Stok Durumu Hakkında',
      message: `Paketinizdeki bazı ürünlerin stok durumu sınırlıdır. Bu ürünler:
        ${outOfStock.map(item => `\n- ${item.name}`).join('')}
        \nBu ürünler için siparişiniz alınacak ve en kısa sürede tedarik edilerek gönderilecektir.`,
      type: 'warning'
    };
  };

  const value = {
    items: state.items,
    subscriptionType: state.subscriptionType,
    step: state.step,
    totalItems: state.items.reduce((sum, item) => sum + item.quantity, 0),
    totalPrice: state.items.reduce(
      (sum, item) => {
        const price = item.discountedPrice || item.price;
        return sum + (price * item.quantity);
      },
      0
    ),
    subscriptionPlan: state.subscriptionPlan,
    outOfStockItems: state.outOfStockItems,
    orderInfo: state.orderInfo,
    isEditing: state.isEditing,
    addToPackage: (product) => {
      dispatch({ type: 'ADD_TO_PACKAGE', payload: product });
    },
    removeFromPackage: (productId) => {
      dispatch({ type: 'REMOVE_FROM_PACKAGE', payload: productId });
    },
    updateQuantity: (productId, quantity) => {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
    },
    setSubscriptionType: (type) => {
      dispatch({ type: 'SET_SUBSCRIPTION_TYPE', payload: type });
    },
    setStep: (step) => {
      dispatch({ type: 'SET_STEP', payload: step });
    },
    resetPackage: () => {
      dispatch({ type: 'RESET_PACKAGE' });
    },
    setSubscriptionPlan: (plan) => {
      dispatch({ type: 'SET_SUBSCRIPTION_PLAN', payload: plan });
    },
    setOrderInfo: (orderInfo) => {
      dispatch({ type: 'SET_ORDER_INFO', payload: orderInfo });
    },
    setIsEditing: (isEditing) => {
      dispatch({ type: 'SET_IS_EDITING', payload: isEditing });
    },
    checkStockStatus,
    getStockWarningMessage,
    hasStockWarning: () => state.outOfStockItems?.length > 0 || false,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
} 